import type { FocusEventHandler } from 'react';

import Badge from 'components/Badge';
import Icon from 'components/Icon';
import PlainButton from 'components/PlainButton';
import { cn } from 'utils/classNames';
import { getTestDataAttrFrom, is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

export type Size = 'lg' | 'md' | 'sm';

interface QuantityButtonProps {
	disabled?: boolean;
	onClick: () => void;
	size: Size;
	variant: 'decrease' | 'increase';
}

function QuantityButton({
	disabled,
	onClick,
	size,
	variant,
}: QuantityButtonProps) {
	const { t } = useI18n();

	return (
		<PlainButton
			className={cn(
				'relative z-1 inline-block shrink-0 rounded-full',
				!disabled &&
					'hover:bg-baseHover hover:forced-colors-outline focus-visible:bg-baseHover',
				disabled && 'text-disabled',
				size === 'sm' && 'size-7.5',
				size === 'md' && 'size-10.5',
				size === 'lg' && 'size-12.5',
				'after:absolute after:-inset-y-1',
				variant === 'increase'
					? 'after:-right-1 after:left-0 after:rounded-r-full'
					: 'after:right-0 after:-left-1 after:rounded-l-full',
			)}
			onClick={onClick}
			disabled={disabled}
			data-cy={getTestDataAttrFrom(`quantity-${variant}`)}
			aria-label={
				variant === 'increase'
					? t('product_quantity_increase_by_one_label')
					: t('product_quantity_decrease_by_one_label')
			}
		>
			<Icon name={variant === 'increase' ? 'plus' : 'minus'} />
		</PlainButton>
	);
}
QuantityButton.displayName = 'StepInput_QuantityButton';

interface Props {
	className?: string;
	id: string;
	invalid?: boolean;
	minValue?: number;
	onChange: (qty: number | undefined) => void;
	onInputBlur?: FocusEventHandler<HTMLInputElement>;
	size?: Size;
	value: number | undefined;
}

export default function StepInput({
	className,
	id,
	invalid = false,
	minValue = 1,
	onChange,
	onInputBlur,
	size = 'md',
	value,
}: Props) {
	const { t } = useI18n();

	const increaseValue = () => {
		onChange((value ?? minValue - 1) + 1);
	};
	const decreaseValue = () => {
		const newValue = Math.max((value ?? 0) - 1, minValue);
		// Also check current value to avoid unnecessary onChange triggers.
		if (newValue >= minValue && value !== newValue) {
			onChange(newValue);
		}
	};

	return (
		<div
			className={cn(
				'inline-flex items-center rounded-full border bg-base px-0.5',
				!invalid &&
					'border-strong has-[input:hover]:outline-1 has-[input:hover]:outline-strong',
				invalid && 'relative border-error outline-1 outline-error',
				'has-[input:focus-visible]:focus-outline',
				size === 'sm' && 'h-9',
				size === 'md' && 'h-12',
				size === 'lg' && 'h-14',
				className,
			)}
		>
			<QuantityButton
				onClick={decreaseValue}
				disabled={value === minValue}
				size={size}
				variant="decrease"
			/>
			<label htmlFor={`product-quantity-${id}`} className="sr-only">
				{t('general_quantity_label')}
			</label>
			<input
				className={cn(
					'block w-[3em] min-w-0 flex-1 self-stretch text-center focus-outline-none',
					size === 'sm' && 'text-sm',
				)}
				id={`product-quantity-${id}`}
				inputMode="numeric"
				type="text"
				pattern="\d*"
				// Styles will be more specific, but set as a reasonable default.
				size={4}
				name="product-quantity"
				onKeyDown={({ key }) => {
					if (key === 'ArrowUp') {
						increaseValue();
					} else if (key === 'ArrowDown') {
						decreaseValue();
					}
				}}
				onBlur={onInputBlur}
				onChange={(event) => {
					// Currently doesn't handle a minValue larger than 9, but it's
					// unlikely to be needed.
					const val = event.target.value;
					const numVal = Number.parseInt(val, 10);
					if (val !== '' && is.number(numVal) && numVal >= minValue) {
						onChange(numVal);
					} else {
						onChange(undefined);
					}
				}}
				value={value ?? ''}
			/>
			<QuantityButton onClick={increaseValue} size={size} variant="increase" />
			{invalid && (
				<Badge
					icon="error"
					variant="error"
					size={size === 'sm' ? 'sm' : 'md'}
					className={cn(
						'pointer-events-none absolute z-2',
						size === 'lg' ? '-top-0.5 -right-0.5' : '-top-1 -right-1',
					)}
				/>
			)}
		</div>
	);
}
StepInput.displayName = 'StepInput';
